// Governance / Agent Council özet kartı — Genel Bakış cockpit'ine gömülü slot.
// EFQM Kriteri 4 (Yönetişim).
//
// Sorumluluk:
// - Açık council kararı sayısı + toplam.
// - Risk + status dağılımı (chip).
// - Son kararın decisionType / subject / reason / createdAt değerleri.
// - GET /api/v2/council/decisions endpoint sağlık badge'i (HTTP + latency).
//
// Read-only disiplin:
// - Vote / approve / execute tetikleyici YOK.
// - Council decision backend mantığı değişmez.
// - Sadece "Tüm kararlar →" linki #nav-agent-council butonunu programatik tıklatır.
//
// Slot: dashboard.html → view-genel-bakis içine genel-bakis.ts render'ı tarafından
// `<div id="gb-governance-card"></div>` enjekte ediliyor. Bu modül o id'yi hedefler.

import { escHtml } from '../utils.js';
import { apiFetch } from '../api.js';
import type { CouncilDecision, CouncilSummary } from './agent-council.js';

interface HealthOk {
  kind: 'ok';
  httpStatus: number;
  latencyMs: number;
  fetchedAt: string;
  decisions: CouncilDecision[];
  summary: CouncilSummary;
}
interface HealthFail {
  kind: 'error';
  httpStatus: number | null;
  latencyMs: number;
  fetchedAt: string;
  message: string;
}
type State =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | HealthOk
  | HealthFail;

let state: State = { kind: 'idle' };

export async function loadGovernanceSummary(): Promise<void> {
  state = { kind: 'loading' };
  const started = performance.now();
  const fetchedAt = new Date().toISOString();
  try {
    const res = await apiFetch('/api/v2/council/decisions?limit=5');
    const latencyMs = Math.round(performance.now() - started);
    let body: unknown;
    try { body = await res.json(); }
    catch {
      state = { kind: 'error', httpStatus: res.status, latencyMs, fetchedAt, message: 'invalid_json' };
      return;
    }
    const obj = body as {
      ok?: boolean;
      error?: string;
      message?: string;
      decisions?: CouncilDecision[];
      summary?: CouncilSummary;
    };
    if (!res.ok || obj?.ok !== true || !obj.decisions || !obj.summary) {
      state = {
        kind: 'error',
        httpStatus: res.status,
        latencyMs,
        fetchedAt,
        message: obj?.message ?? obj?.error ?? `http_${res.status}`,
      };
      return;
    }
    state = {
      kind: 'ok',
      httpStatus: res.status,
      latencyMs,
      fetchedAt,
      decisions: obj.decisions,
      summary: obj.summary,
    };
  } catch (err) {
    const latencyMs = Math.round(performance.now() - started);
    state = {
      kind: 'error',
      httpStatus: null,
      latencyMs,
      fetchedAt,
      message: (err as Error).message || 'network_error',
    };
  }
}

export function renderGovernanceSummary(): void {
  // Slot Genel Bakış view'ı render'ı tarafından sahnelendi. View başka sekmede
  // ise slot DOM'da olmayabilir — sessizce noop.
  const root = document.getElementById('gb-governance-card');
  if (!root) return;
  root.innerHTML = renderGovernanceSummaryHtml(state);
}

export function renderGovernanceSummaryHtml(s: State): string {
  if (s.kind === 'idle') return '';
  const card = (inner: string) => `
    <div class="gb-card" style="margin:0 0 12px;border-left:3px solid var(--gem-amber,#fbbf24);">
      <div class="gb-card-title" style="display:flex;align-items:center;justify-content:space-between;gap:8px;flex-wrap:wrap;">
        <span>🗳️ Yönetişim — Agent Council</span>
        ${healthBadge(s)}
      </div>
      ${inner}
    </div>`;

  if (s.kind === 'loading') {
    return card('<div class="gb-empty-sm">Yükleniyor…</div>');
  }
  if (s.kind === 'error') {
    return card(`<div class="gb-empty-sm" style="color:var(--gem-ruby,#f87171);">Endpoint okunamadı: ${escHtml(s.message)}</div>`);
  }

  const open = s.summary.byStatus?.open ?? 0;
  const latest = s.decisions[0];
  const riskChips = chipRow('Risk', s.summary.byRiskLevel);
  const statusChips = chipRow('Status', s.summary.byStatus);

  const latestBlock = latest
    ? `<div style="display:grid;grid-template-columns:auto 1fr;gap:4px 12px;font-size:12px;margin-top:8px;">
         <span style="color:var(--muted);">Son karar:</span>
         <span><code style="font-size:11px;">${escHtml(latest.decisionType)}</code> · ${escHtml(latest.subjectType)}/${escHtml(latest.subjectId.slice(0, 8))} · <span style="opacity:.8;">${escHtml(latest.status)}</span></span>
         <span style="color:var(--muted);">Gerekçe:</span>
         <span>${escHtml(latest.reason ?? '—')}</span>
         <span style="color:var(--muted);">createdAt:</span>
         <span style="font-variant-numeric:tabular-nums;">${escHtml(latest.createdAt)}</span>
       </div>`
    : '<div class="gb-empty-sm" style="margin-top:8px;">Henüz council kararı yok.</div>';

  return card(`
    <div style="display:flex;flex-wrap:wrap;gap:14px;font-size:12px;align-items:center;">
      <span><strong style="font-size:18px;">${open}</strong> <span style="color:var(--muted);">açık karar</span></span>
      <span style="color:var(--muted);">·</span>
      <span><strong>${s.summary.total}</strong> <span style="color:var(--muted);">toplam</span></span>
      ${riskChips}
      ${statusChips}
      <a href="#" id="gov-go-council" style="margin-left:auto;font-size:12px;">Tüm kararlar →</a>
    </div>
    ${latestBlock}
  `);
}

function healthBadge(s: State): string {
  if (s.kind === 'idle' || s.kind === 'loading') {
    return '<span style="font-size:11px;padding:2px 8px;border-radius:8px;background:#3a3a3a;color:#bbb;">…</span>';
  }
  const lat = `${s.latencyMs}ms`;
  if (s.kind === 'ok') {
    return `<span title="GET /api/v2/council/decisions · ${s.httpStatus} · ${escHtml(s.fetchedAt)}" style="font-size:11px;padding:2px 8px;border-radius:8px;background:#1f4a2a;color:#9be8a8;">● API ${s.httpStatus} · ${lat}</span>`;
  }
  const code = s.httpStatus ?? 'ERR';
  return `<span title="GET /api/v2/council/decisions · ${escHtml(s.fetchedAt)} · ${escHtml(s.message)}" style="font-size:11px;padding:2px 8px;border-radius:8px;background:#4a1f1f;color:#f0a8a8;">● API ${code} · ${lat}</span>`;
}

function chipRow(label: string, m: Record<string, number> | undefined): string {
  const entries = Object.entries(m ?? {});
  if (!entries.length) return '';
  return `<span><span style="color:var(--muted);">${escHtml(label)}:</span> ${entries.map(([k, v]) => `<span style="background:var(--bg2,#222);padding:2px 6px;border-radius:8px;margin-right:4px;">${escHtml(k)} <strong>${v}</strong></span>`).join('')}</span>`;
}

export function bindGovernanceSummaryDelegation(): void {
  // Slot Genel Bakış her render'ında yeniden çiziliyor; delegation document-level
  // capture ile yapılır, slot'un yeniden enjekte edilmesi listener'ı kaybetmez.
  document.addEventListener('click', (ev) => {
    const target = ev.target as HTMLElement | null;
    if (!target) return;
    if (target.id === 'gov-go-council' || target.closest?.('#gov-go-council')) {
      ev.preventDefault();
      document.getElementById('nav-agent-council')?.click();
    }
  });
}
